import dayjs from 'dayjs';
import { useState } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import GeneralDialog from '../../../../../shared/general-dialog';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Check from '@mui/icons-material/Check';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';

interface RoleMembershipExpirationChipProps {
  currentExpirationTime?: number;
  disabled?: boolean;
  isUpdating?: boolean;
  onExpirationChange: (newExpirationTime: number | null) => void;
}

export default function RoleMembershipExpirationChip({
  currentExpirationTime,
  disabled,
  isUpdating,
  onExpirationChange,
}: RoleMembershipExpirationChipProps) {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [expirationTime, setExpirationTime] = useState<number>(0);

  const handleMenuClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
  };

  function handleSubmit(type: 'create' | 'delete' | 'update') {
    if (type === 'delete') {
      onExpirationChange(null);
    } else {
      onExpirationChange(expirationTime);
    }
    handleMenuClose();
  }

  const expiryDateLabel = currentExpirationTime
    ? `Access expires ${dayjs(currentExpirationTime).format('MMMM D, hA')}`
    : 'Click to set expiration';

  const isAboutToExpire = currentExpirationTime
    ? dayjs(currentExpirationTime).diff(dayjs(), 'hours') <= 24
    : false;

  const chipBgColor = currentExpirationTime
    ? isAboutToExpire
      ? '#E0982D'
      : '#6FC6AC'
    : undefined;

  const isExpirationInvalid =
    expirationTime !== null &&
    (!dayjs(expirationTime).isValid() ||
      dayjs(expirationTime).isBefore(dayjs()));

  return (
    <>
      <span>
        <Chip
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box>{expiryDateLabel}</Box>
              {currentExpirationTime && !disabled && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Edit fontSize="small" sx={{ fontSize: '1.1rem' }} />
                  {isUpdating ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Delete
                      fontSize="small"
                      sx={{ fontSize: '1.1rem', cursor: 'pointer' }}
                      onClick={(event) => {
                        event.stopPropagation();
                        setIsDeleteDialogOpen(true);
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
          }
          size="small"
          sx={{
            width: 'fit-content',
            cursor: disabled || isUpdating ? 'not-allowed' : 'pointer',
            backgroundColor: chipBgColor,
            color: currentExpirationTime ? 'white' : undefined,
            paddingLeft: currentExpirationTime && !disabled ? 0.5 : undefined,
          }}
          disabled={disabled || isUpdating}
          onClick={(event) => {
            if (disabled || isUpdating) return;
            setExpirationTime(
              currentExpirationTime ||
                dayjs().add(1, 'M').endOf('day').valueOf()
            );
            setIsOpen(true);
            setAnchorEl(event.currentTarget);
            event.stopPropagation();
          }}
        />
      </span>
      <Menu
        id="date-picker-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              width: 290,
            },
          },
        }}
      >
        <Stack sx={{ p: 1 }} flexDirection="row" gap={1} alignItems="center">
          <DateTimePicker
            label={
              isExpirationInvalid
                ? 'Expiration Date cannot be in the past'
                : 'Expiration Date'
            }
            views={['year', 'month', 'day', 'hours']}
            sx={{ flexGrow: 1 }}
            value={dayjs(expirationTime)}
            timezone="system"
            disablePast
            minDateTime={dayjs()}
            onChange={(e) => {
              if (e) {
                setExpirationTime(e.valueOf());
              }
            }}
            slotProps={{
              textField: {
                error: isExpirationInvalid,
              },
            }}
          />{' '}
          {isUpdating ? (
            <Box sx={{ px: 1 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <IconButton
              disabled={isExpirationInvalid}
              onClick={(event) => {
                event.stopPropagation();
                if (!currentExpirationTime) {
                  handleSubmit('create');
                } else {
                  handleSubmit('update');
                }
              }}
            >
              <Check />
            </IconButton>
          )}{' '}
        </Stack>
      </Menu>

      <GeneralDialog
        open={isDeleteDialogOpen}
        handleClose={() => setIsDeleteDialogOpen(false)}
        title="Remove Expiration Date?"
        dialogContent={
          <Typography>
            This role will no longer have a set end date. The user will retain
            this access until it is manually revoked.
          </Typography>
        }
        primaryButton={{
          title: 'Remove Date',
          action: () => {
            handleSubmit('delete');
            setIsDeleteDialogOpen(false);
          },
          loading: false,
          disabled: false,
          color: isAboutToExpire ? 'warning' : 'primary',
        }}
        secondaryButton={{
          title: 'Cancel',
          action: () => setIsDeleteDialogOpen(false),
          loading: false,
          disabled: false,
          shown: true,
        }}
      />
    </>
  );
}
